import {
  OFFER_TYPE_CARHIRE_SLUG,
  OFFER_TYPE_CRUISE_SLUG,
  OFFER_TYPE_HOTEL_SLUG,
  OFFER_TYPE_TOUR_SLUG,
  PRODUCT_TYPE_ULTRALUX_SLUG,
} from 'constants/offer'
import { LUXURY_PLUS, LUXURY_PLUS_PATHS } from 'luxPlus/constants/base'
import { ASIAN_COUNTRIES, FLIGHTS_DISABLED_GEO_STATES } from './region'
import config from '.'
import { LOYALTY_CREDIT_CARD_PAGE, LOYALTY_LANDING_PAGE } from 'loyalty/constants/loyaltyRoutes'
import { LUXURY_ESCAPES } from 'constants/brands'
import { FRIENDS_AND_FAMILY_DISCOUNT_PAGE_SLUG } from 'constants/friendsAndFamilyDiscount'
import { OptimizelyFeatureFlags } from 'constants/featureFlags'
import { LabFeatureFlags } from 'constants/lab'
import { LUX_PLUS_MONTH_LANDING_PAGE_URL } from 'takeovers/luxPlusMonthTakeover/constants/luxPlusMonthTakeoverConstants'
import { TakeoverId } from 'lib/lab/dynamicPayload'
import { LE_HOMES_AND_VILLAS_TO } from 'constants/offerTypesList'

const HOTELS_NAV_ITEM: App.NavItem = {
  to: `/${OFFER_TYPE_HOTEL_SLUG}`,
  id: `nav-link-${OFFER_TYPE_HOTEL_SLUG}`,
  text: 'Hotels',
  testId: 'nav-link-hotels',
  iconName: 'bed',
  mobileNav: true,
  category: 'primary',
}

const HOMES_AND_VILLAS_NAV_ITEM: App.NavItem = {
  to: LE_HOMES_AND_VILLAS_TO,
  id: 'nav-link-homes-and-villas',
  text: 'Homes & Villas',
  testId: 'nav-link-homes-and-villas',
  iconName: 'home-alt',
  mobileNav: true,
  category: 'primary',
}

const TOURS_NAV_ITEM = (env: App.EnvVarsAndFeatures): App.NavItem => ({
  to: `/${OFFER_TYPE_TOUR_SLUG}`,
  id: `nav-link-${OFFER_TYPE_TOUR_SLUG}`,
  text: 'Tours',
  testId: 'nav-link-tours',
  onlyOnRegions: env.CRUISES_REGIONS?.split(',') ?? [],
  mobileNav: true,
  iconName: 'compass',
  category: 'primary',
})

const TOURS_CRUISES_NAV_ITEM = (env: App.EnvVarsAndFeatures): App.NavItem => ({
  to: `/${OFFER_TYPE_TOUR_SLUG}`,
  id: `nav-link-${OFFER_TYPE_TOUR_SLUG}`,
  text: 'Tours & Cruises',
  testId: 'nav-link-tours',
  notOnRegions: env.CRUISES_REGIONS?.split(',') ?? [],
  mobileNav: true,
  iconName: 'compass',
  category: 'primary',
})

const CRUISES_NAV_ITEM = (env: App.EnvVarsAndFeatures): App.NavItem => ({
  to: `/${OFFER_TYPE_CRUISE_SLUG}`,
  id: `nav-link-${OFFER_TYPE_CRUISE_SLUG}`,
  text: 'Cruises',
  onlyOnRegions: env.CRUISES_REGIONS?.split(','),
  mobileNav: true,
  testId: 'nav-link-cruises',
  featureToggle: 'CRUISES_STANDLONE_ENABLED',
  iconName: 'ship',
  category: 'primary',
})

const FLIGHTS_NAV_ITEM: App.NavItem = {
  to: '/flights',
  id: 'nav-link-flights',
  text: 'Flights',
  onlyOnRegions: ['AU', 'NZ', 'GB', 'IN', 'SG', 'HK', 'US'],
  notOnGeoStates: FLIGHTS_DISABLED_GEO_STATES,
  mobileNav: true,
  iconName: 'plane-departure',
  category: 'primary',
}

const CAR_HIRE_NAV_ITEM = (env: App.EnvVarsAndFeatures): App.NavItem => ({
  to: `/${OFFER_TYPE_CARHIRE_SLUG}`,
  id: `nav-link-${OFFER_TYPE_CARHIRE_SLUG}`,
  text: 'Car Hire',
  testId: 'nav-link-car-hire',
  onlyOnRegions: env.CARHIRE_REGIONS.split(','),
  mobileNav: true,
  iconName: 'car',
  featureToggle: 'CARHIRE_ENABLED',
  category: 'primary',
})

const CARS_NAV_ITEM = (env: App.EnvVarsAndFeatures): App.NavItem => ({
  to: '/cars',
  id: 'nav-link-cars',
  text: 'Cars',
  testId: 'nav-link-cars',
  onlyOnRegions: env.CARHIRE_REGIONS.split(','),
  mobileNav: true,
  newLabel: true,
  iconName: 'car',
  featureToggle: 'CARS_PAGE_ENABLED',
  category: 'primary',
})

const CAR_NAV_ITEM = (env: App.EnvVarsAndFeatures): App.NavItem => (
  env.CARS_PAGE_ENABLED && env.CARHIRE_ENABLED ? CARS_NAV_ITEM(env) : CAR_HIRE_NAV_ITEM(env)
)

const ULTRA_LUX_NAV_ITEM: App.NavItem = {
  to: `/${PRODUCT_TYPE_ULTRALUX_SLUG}`,
  id: 'nav-link-ultralux',
  text: 'Ultra Lux',
  mobileNav: true,
  testId: 'nav-link-ultralux',
  featureToggle: 'ULTRALUX_ENABLED',
  iconName: 'ultra-lux-diamond',
  component: 'secondary',
  category: 'tertiary',
}

const EXPERIENCES_NAV_ITEM: App.NavItem = {
  to: '/experiences',
  id: 'nav-link-experiences',
  text: 'Experiences',
  notOnRegions: ASIAN_COUNTRIES.filter(c => !['SG', 'ID', 'TH', 'VN', 'AE'].includes(c)),
  mobileNav: true,
  iconName: 'hot-air-balloon',
  category: 'primary',
}

const getMaldivesCharterNavRegions = (env: App.EnvVarsAndFeatures) => (
  env.BRAND === LUXURY_ESCAPES ? (env.agentHub?.isEnabled ? ['AU', 'NZ'] : ['AU']) : []
)

const MALDIVES_PACKAGES_NAV_ITEM = (env: App.EnvVarsAndFeatures): App.NavItem => ({
  to: '/campaign/maldives-direct',
  id: 'nav-link-maldives-packages',
  text: 'Maldives Bundles',
  testId: 'nav-link-maldives-packages',
  mobileNav: true,
  onlyOnRegions: getMaldivesCharterNavRegions(env),
  iconName: 'island',
  category: 'primary',
})

const LUX_PLUS_MONTH_EVENT_NAV_ITEM: App.NavItem = {
  to: LUX_PLUS_MONTH_LANDING_PAGE_URL,
  id: 'nav-link-luxplus-month-event',
  text: 'LuxPlus+ Member Event',
  newLabel: true,
  testId: 'nav-link-luxplus-month-event',
  mobileNav: true,
  iconName: 'sparkles',
  labCampaignFeatureToggle: TakeoverId.LUX_PLUS_MONTH_TAKEOVER,
  category: 'primary',
}

const FRIENDS_AND_FAMILY_INVITE_NAV_ITEM = (env: App.EnvVarsAndFeatures): App.NavItem => ({
  to: `/campaign/${FRIENDS_AND_FAMILY_DISCOUNT_PAGE_SLUG}`,
  id: 'nav-link-friends-and-family',
  text: 'Friends & Family Discount',
  testId: 'nav-link-friends-and-family',
  mobileNav: true,
  onlyOnRegions: getMaldivesCharterNavRegions(env),
  accountToggle: 'memberId',
  staffOnly: true,
  optimizelyFeatureToggle: OptimizelyFeatureFlags.maldivesCharterFnfPage,
  iconName: 'smile',
  category: 'tertiary',
})

const LUXURY_PLUS_NAV_ITEM: App.NavItem = {
  to: `/${LUXURY_PLUS_PATHS.LANDING_PAGE}`,
  id: 'nav-link-luxury-plus',
  newLabel: true,
  text: LUXURY_PLUS.PROGRAM_NAME,
  mobileNav: true,
  luxPlusToggle: 'luxPlusEnabled',
  sideNavIconName: 'plus',
  category: 'secondary',
}

const LOYALTY_NAV_ITEM: App.NavItem = {
  to: `/${LOYALTY_LANDING_PAGE}`,
  id: 'nav-link-lux-loyalty',
  newLabel: true,
  text: 'Société',
  mobileNav: true,
  loyaltyToggle: 'luxLoyaltyToggle',
  category: 'secondary',
}

const DREAM_MAGAZINE_NAV_ITEM: App.NavItem = {
  href: 'https://luxuryescapes.com/inspiration/',
  target: '_blank',
  id: 'nav-link-dream-magazine',
  text: 'Inspiration',
  testId: 'nav-link-dream-magazine',
  onlyOnLanguages: ['en'],
  hideInStoreMode: true,
  mobileNav: true,
  iconName: 'magazine',
  category: 'primary',
  optimizelyFeatureToggle: OptimizelyFeatureFlags.showDreamBlogLinks,
}

const SUPPORT_NAV_ITEM: App.NavItem = {
  to: '/support',
  id: 'nav-link-support',
  text: 'Support',
  mobileNav: true,
  hideInStoreMode: true,
  component: 'secondary',
  iconName: 'comments',
  category: 'tertiary',
}

const STORE_VISIT_NAV_ITEM: App.NavItem = {
  href: 'https://luxuryescapes.com/au/campaign/store',
  id: 'nav-link-store',
  text: 'Visit Us In Store',
  mobileNav: true,
  component: 'secondary',
  iconName: 'store',
  onlyOnRegions: ['AU'],
  category: 'tertiary',
}

const GIFT_CARD_NAV_ITEM: App.NavItem = {
  to: '/gift-cards',
  id: 'nav-link-gift-cards',
  text: 'Gift Cards',
  testId: 'nav-link-gift-cards',
  mobileNav: true,
  iconName: 'gift',
  category: 'secondary',
}

/*
 * Shared by the desktop products bar and mobile campaign row. Position after Gift Cards so
 * width-based desktop overflow determines whether it enters "More". Carries no "NEW" badge: the
 * design only badges the hamburger row, and this item feeds both surfaces, so the badge is read
 * where it renders instead (see `useLoyaltyCreditCardNewBadge`).
 */
const SOCIETE_CREDIT_CARD_NAV_ITEM: App.NavItem = {
  to: `/${LOYALTY_CREDIT_CARD_PAGE}`,
  id: 'nav-link-societe-credit-card',
  text: 'Société Credit Card',
  testId: 'nav-link-societe-credit-card',
  mobileNav: true,
  onlyOnRegions: ['AU'],
  labFeatureToggle: LabFeatureFlags.societeCreditCardFlag,
  category: 'tertiary',
}

const PURCHASE_CREDITS_NAV_ITEM: App.NavItem = {
  to: '/purchase-credits',
  id: 'nav-link-purchase-credits',
  text: 'Purchase Credits',
  testId: 'nav-link-purchase-credits',
  mobileNav: true,
  accountToggle: 'isSpoofed',
  iconName: 'usd',
  category: 'tertiary',
}

const INSURANCE_NAV_ITEM: App.NavItem = {
  to: '/travel-protection',
  id: 'nav-link-insurance',
  text: 'Travel Protection',
  onlyOnRegions: ['AU', 'SG', 'HK', 'IN', 'MY', 'TH'],
  iconName: 'shield',
  category: 'secondary',
}

const MARKETPLACE_NAV_ITEM: App.NavItem = {
  to: '/shop',
  id: 'nav-link-marketplace',
  text: 'Shop',
  onlyOnRegions: ['AU'],
  onlyOnLanguages: ['en'],
  category: 'primary',
  optimizelyFeatureToggle: 'merchandise_launch',
  iconName: 'shop',
}

const REFER_NAV_ITEM: App.NavItem = {
  to: '/refer',
  id: 'nav-link-refer',
  text: 'Refer a Friend',
  accountToggle: 'referralProgramEnabled',
  testId: 'nav-link-refer',
  iconName: 'user-arrows',
  category: 'tertiary',
}

const LE4B_NAV_ITEM: App.NavItem = {
  href: 'https://lebusinesstraveller.com/',
  target: '_blank',
  id: 'nav-link-le4b',
  text: 'Business Travel',
  onlyOnRegions: ['AU'],
  iconName: 'briefcase',
  category: 'tertiary',
}

const GOLF_NAV_ITEM_ALL_REGIONS: App.NavItem = {
  to: '/campaign/golf',
  id: 'nav-link-golf',
  text: 'Golf',
  iconName: 'golf',
  category: 'tertiary',
}

const GROUPS_NAV_ITEM: App.NavItem = {
  href: 'https://luxuryescapes.com/inspiration/group-bookings/',
  target: '_blank',
  id: 'nav-link-groups',
  text: 'Groups',
  onlyOnLanguages: ['en'],
  mobileNav: true,
  iconName: 'users-multi',
  category: 'tertiary',
}

const WEDDINGS_NAV_ITEM: App.NavItem = {
  to: '/weddings',
  id: 'nav-link-weddings',
  text: 'Weddings',
  mobileNav: true,
  iconName: 'calendar-heart',
  category: 'tertiary',
}

const MORE_DROPDOWN_NAV_ITEM = (items: Array<App.NavItem>): App.NavItem => ({
  id: 'nav-link-more',
  text: 'More',
  hasDropdown: true,
  testId: 'nav-link-more',
  mobileNav: true,
  hideOnTablet: true,
  component: 'secondary',
  items,
  category: 'tertiary',
})

export function getNavItemsFlat(): Array<App.NavItem> {
  return ([
    HOTELS_NAV_ITEM,
    HOMES_AND_VILLAS_NAV_ITEM,
    LUX_PLUS_MONTH_EVENT_NAV_ITEM,
    MALDIVES_PACKAGES_NAV_ITEM(config),
    FLIGHTS_NAV_ITEM,
    TOURS_NAV_ITEM(config),
    CRUISES_NAV_ITEM(config),
    TOURS_CRUISES_NAV_ITEM(config),
    DREAM_MAGAZINE_NAV_ITEM,
    EXPERIENCES_NAV_ITEM,
    MARKETPLACE_NAV_ITEM,
    CAR_NAV_ITEM(config),
    ULTRA_LUX_NAV_ITEM,
    GIFT_CARD_NAV_ITEM,
    SOCIETE_CREDIT_CARD_NAV_ITEM,
    PURCHASE_CREDITS_NAV_ITEM,
    SUPPORT_NAV_ITEM,
    STORE_VISIT_NAV_ITEM,
    INSURANCE_NAV_ITEM,
    GOLF_NAV_ITEM_ALL_REGIONS,
    REFER_NAV_ITEM,
    GROUPS_NAV_ITEM,
    WEDDINGS_NAV_ITEM,
    LE4B_NAV_ITEM,
    FRIENDS_AND_FAMILY_INVITE_NAV_ITEM(config),
  ])
}

export function getOrderedItemsByRegion(env: App.EnvVarsAndFeatures): Record<string, Array<App.NavItem>> {
  return {
    AU: [
      HOTELS_NAV_ITEM,
      HOMES_AND_VILLAS_NAV_ITEM,
      LUX_PLUS_MONTH_EVENT_NAV_ITEM,
      MALDIVES_PACKAGES_NAV_ITEM(env),
      LUXURY_PLUS_NAV_ITEM,
      LOYALTY_NAV_ITEM,
      FLIGHTS_NAV_ITEM,
      TOURS_NAV_ITEM(env),
      CRUISES_NAV_ITEM(env),
      DREAM_MAGAZINE_NAV_ITEM,
      EXPERIENCES_NAV_ITEM,
      GIFT_CARD_NAV_ITEM,
      ULTRA_LUX_NAV_ITEM,
      SUPPORT_NAV_ITEM,
      MORE_DROPDOWN_NAV_ITEM([
        PURCHASE_CREDITS_NAV_ITEM,
        STORE_VISIT_NAV_ITEM,
        CAR_NAV_ITEM(env),
        INSURANCE_NAV_ITEM,
        GOLF_NAV_ITEM_ALL_REGIONS,
        REFER_NAV_ITEM,
        GROUPS_NAV_ITEM,
        WEDDINGS_NAV_ITEM,
        LE4B_NAV_ITEM,
        FRIENDS_AND_FAMILY_INVITE_NAV_ITEM(env),
      ]),
    ],
    NZ: [
      HOTELS_NAV_ITEM,
      HOMES_AND_VILLAS_NAV_ITEM,
      LUX_PLUS_MONTH_EVENT_NAV_ITEM,
      MALDIVES_PACKAGES_NAV_ITEM(env),
      LUXURY_PLUS_NAV_ITEM,
      LOYALTY_NAV_ITEM,
      FLIGHTS_NAV_ITEM,
      TOURS_NAV_ITEM(env),
      CRUISES_NAV_ITEM(env),
      DREAM_MAGAZINE_NAV_ITEM,
      EXPERIENCES_NAV_ITEM,
      GIFT_CARD_NAV_ITEM,
      ULTRA_LUX_NAV_ITEM,
      SUPPORT_NAV_ITEM,
      MORE_DROPDOWN_NAV_ITEM([
        PURCHASE_CREDITS_NAV_ITEM,
        CAR_NAV_ITEM(env),
        GOLF_NAV_ITEM_ALL_REGIONS,
        REFER_NAV_ITEM,
        GROUPS_NAV_ITEM,
        WEDDINGS_NAV_ITEM,
        FRIENDS_AND_FAMILY_INVITE_NAV_ITEM(env),
      ]),
    ],
    GB: [
      HOTELS_NAV_ITEM,
      HOMES_AND_VILLAS_NAV_ITEM,
      LUX_PLUS_MONTH_EVENT_NAV_ITEM,
      LUXURY_PLUS_NAV_ITEM,
      LOYALTY_NAV_ITEM,
      FLIGHTS_NAV_ITEM,
      TOURS_CRUISES_NAV_ITEM(env),
      DREAM_MAGAZINE_NAV_ITEM,
      EXPERIENCES_NAV_ITEM,
      GIFT_CARD_NAV_ITEM,
      ULTRA_LUX_NAV_ITEM,
      SUPPORT_NAV_ITEM,
      MORE_DROPDOWN_NAV_ITEM([
        PURCHASE_CREDITS_NAV_ITEM,
        CAR_NAV_ITEM(env),
        GOLF_NAV_ITEM_ALL_REGIONS,
        REFER_NAV_ITEM,
        GROUPS_NAV_ITEM,
        WEDDINGS_NAV_ITEM,
      ]),
    ],
    US: [
      HOTELS_NAV_ITEM,
      HOMES_AND_VILLAS_NAV_ITEM,
      LUX_PLUS_MONTH_EVENT_NAV_ITEM,
      LUXURY_PLUS_NAV_ITEM,
      LOYALTY_NAV_ITEM,
      FLIGHTS_NAV_ITEM,
      TOURS_CRUISES_NAV_ITEM(env),
      DREAM_MAGAZINE_NAV_ITEM,
      EXPERIENCES_NAV_ITEM,
      GIFT_CARD_NAV_ITEM,
      ULTRA_LUX_NAV_ITEM,
      SUPPORT_NAV_ITEM,
      MORE_DROPDOWN_NAV_ITEM([
        PURCHASE_CREDITS_NAV_ITEM,
        CAR_NAV_ITEM(env),
        GOLF_NAV_ITEM_ALL_REGIONS,
        REFER_NAV_ITEM,
        GROUPS_NAV_ITEM,
        WEDDINGS_NAV_ITEM,
      ]),
    ],
    SG: [
      HOTELS_NAV_ITEM,
      HOMES_AND_VILLAS_NAV_ITEM,
      LUX_PLUS_MONTH_EVENT_NAV_ITEM,
      LUXURY_PLUS_NAV_ITEM,
      LOYALTY_NAV_ITEM,
      FLIGHTS_NAV_ITEM,
      TOURS_CRUISES_NAV_ITEM(env),
      DREAM_MAGAZINE_NAV_ITEM,
      EXPERIENCES_NAV_ITEM,
      GIFT_CARD_NAV_ITEM,
      ULTRA_LUX_NAV_ITEM,
      SUPPORT_NAV_ITEM,
      MORE_DROPDOWN_NAV_ITEM([
        PURCHASE_CREDITS_NAV_ITEM,
        CAR_NAV_ITEM(env),
        GOLF_NAV_ITEM_ALL_REGIONS,
        REFER_NAV_ITEM,
        GROUPS_NAV_ITEM,
        WEDDINGS_NAV_ITEM,
      ]),
    ],
    IN: [
      HOTELS_NAV_ITEM,
      HOMES_AND_VILLAS_NAV_ITEM,
      LUX_PLUS_MONTH_EVENT_NAV_ITEM,
      TOURS_CRUISES_NAV_ITEM(env),
      DREAM_MAGAZINE_NAV_ITEM,
      FLIGHTS_NAV_ITEM,
      LUXURY_PLUS_NAV_ITEM,
      LOYALTY_NAV_ITEM,
      ULTRA_LUX_NAV_ITEM,
      SUPPORT_NAV_ITEM,
      MORE_DROPDOWN_NAV_ITEM([
        PURCHASE_CREDITS_NAV_ITEM,
        GIFT_CARD_NAV_ITEM,
        GOLF_NAV_ITEM_ALL_REGIONS,
        REFER_NAV_ITEM,
        GROUPS_NAV_ITEM,
        WEDDINGS_NAV_ITEM,
      ]),
    ],
    DEFAULT: [
      HOTELS_NAV_ITEM,
      HOMES_AND_VILLAS_NAV_ITEM,
      LUX_PLUS_MONTH_EVENT_NAV_ITEM,
      LUXURY_PLUS_NAV_ITEM,
      LOYALTY_NAV_ITEM,
      FLIGHTS_NAV_ITEM,
      TOURS_NAV_ITEM(env),
      CRUISES_NAV_ITEM(env),
      TOURS_CRUISES_NAV_ITEM(env),
      DREAM_MAGAZINE_NAV_ITEM,
      EXPERIENCES_NAV_ITEM,
      GIFT_CARD_NAV_ITEM,
      ULTRA_LUX_NAV_ITEM,
      SUPPORT_NAV_ITEM,
      MORE_DROPDOWN_NAV_ITEM([
        PURCHASE_CREDITS_NAV_ITEM,
        CAR_NAV_ITEM(env),
        INSURANCE_NAV_ITEM,
        GOLF_NAV_ITEM_ALL_REGIONS,
        REFER_NAV_ITEM,
        GROUPS_NAV_ITEM,
        WEDDINGS_NAV_ITEM,
      ]),
    ],
  }
}

// This is only used by for three brands: cudotravel, dealstravel, scoopontravel
export function getWhiteLabelsCommonNavItems(env: App.EnvVarsAndFeatures): Array<App.NavItem> {
  const items: Array<App.NavItem> = [
    {
      id: 'hotelsandresorts',
      to: `/${OFFER_TYPE_HOTEL_SLUG}`,
      text: 'Hotels & Resorts',
      iconName: 'bed',
      mobileNav: true,
      category: 'primary',
    },
    ...env.ENABLE_STANDALONE_FLIGHTS ? [{
      to: '/flights',
      id: 'flights',
      text: 'Flights',
      onlyOnRegions: ['AU', 'NZ', 'HK', 'GB'],
      newLabel: true,
      newLabelOnlyOnRegions: ['AU', 'NZ', 'HK', 'GB'],
      iconName: 'plane-departure',
      mobileNav: true,
      category: 'primary',
    } as App.NavItem] : [],
    ...env.EXPERIENCES_STANDLONE_ENABLED ? [{
      id: 'experiences',
      to: '/experiences',
      text: 'Experiences',
      iconName: 'hot-air-balloon',
      mobileNav: true,
      category: 'primary' as App.NavItemCategory,
    } as App.NavItem] : [],
    {
      to: `/${OFFER_TYPE_TOUR_SLUG}`,
      id: `nav-link-${OFFER_TYPE_TOUR_SLUG}`,
      text: 'Tours',
      testId: 'nav-link-tours',
      onlyOnRegions: env.CRUISES_REGIONS?.split(',') ?? [],
      iconName: 'compass',
      mobileNav: true,
      category: 'primary',
    },
    {
      to: `/${OFFER_TYPE_CRUISE_SLUG}`,
      id: `nav-link-${OFFER_TYPE_CRUISE_SLUG}`,
      text: 'Cruises',
      onlyOnRegions: env.CRUISES_REGIONS?.split(','),
      iconName: 'ship',
      mobileNav: true,
      testId: 'nav-link-cruises',
      featureToggle: 'CRUISES_STANDLONE_ENABLED',
      category: 'primary',
    },
    {
      to: `/${OFFER_TYPE_CARHIRE_SLUG}`,
      id: `nav-link-${OFFER_TYPE_CARHIRE_SLUG}`,
      newLabel: true,
      text: 'Car Hire',
      testId: 'nav-link-car-hire',
      onlyOnRegions: env.CARHIRE_REGIONS?.split(','),
      mobileNav: true,
      iconName: 'car',
      featureToggle: 'CARHIRE_ENABLED',
      category: 'primary',
    },
    {
      to: `/${OFFER_TYPE_TOUR_SLUG}`,
      id: `nav-link-${OFFER_TYPE_TOUR_SLUG}`,
      text: 'Tours & Cruises',
      testId: 'nav-link-tours',
      notOnRegions: env.CRUISES_REGIONS?.split(',') ?? [],
      iconName: 'compass',
      mobileNav: true,
      category: 'primary',
    },
    ...env.ENABLE_GIFT_CARD ? [{
      to: '/gift-cards',
      id: 'nav-link-gift-cards',
      text: 'Gift Cards',
      mobileNav: true,
      newLabel: true,
      iconName: 'gift',
      category: 'secondary',
    } as App.NavItem] : [],
    ...env.SUPPORT_ENABLED ? [{
      to: '/support',
      id: 'nav-link-support',
      text: 'Support',
      mobileNav: true,
      hideInStoreMode: true,
      iconName: 'comments',
      category: 'tertiary',
    } as App.NavItem] : [],
  ]
  return items
}
